import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import { useTable, useSortBy, useFilters, useGlobalFilter } from "react-table";
import { useLocation } from "react-router-dom";
import moment from "moment";
import XLSX from "xlsx-js-style";
import { toast } from "react-toastify";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useLanguage } from "../../common/i18n/LanguageContext";

const EXPORT_BORDER = { style: "thin", color: { rgb: "CBD5E1" } };
const EXPORT_THIN_BORDERS = {
  top: EXPORT_BORDER,
  bottom: EXPORT_BORDER,
  left: EXPORT_BORDER,
  right: EXPORT_BORDER,
};

// Fields known to hold plain numeric amounts/counts — only these get
// coerced to real numbers in the export. Everything else (usernames,
// positions, etc.) stays text, so an all-digit username or ID never loses
// formatting (e.g. leading zeros) by silently becoming a number.
const NUMERIC_ACCESSORS = new Set(["downline_count", "member_count", "credit"]);

// Generic export: reads header text + raw values straight from the columns
// definition (any column without an `accessor` is a rendered-only control —
// row number, action buttons — and is skipped), so it works for whichever
// column set the caller passes in without needing to know their meaning.
const buildTableExportWorksheet = (columns, rows) => {
  const exportColumns = columns.filter((col) => col.accessor);
  const header = exportColumns.map((col) => col.Header);

  const dataRows = rows.map((row) =>
    exportColumns.map((col) => {
      const raw = row[col.accessor];
      if (raw === null || raw === undefined || raw === "") return "";
      if (NUMERIC_ACCESSORS.has(col.accessor)) {
        const num = Number(raw);
        if (!isNaN(num)) return num;
      }
      return raw;
    }),
  );

  const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
  worksheet["!cols"] = exportColumns.map(() => ({ wch: 16 }));

  const setStyle = (r, c, style) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!worksheet[ref]) worksheet[ref] = { t: "s", v: "" };
    worksheet[ref].s = { ...(worksheet[ref].s || {}), ...style };
  };

  exportColumns.forEach((_, c) => {
    setStyle(0, c, {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "0D4BB3" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: EXPORT_THIN_BORDERS,
    });
  });

  dataRows.forEach((line, rowIdx) => {
    const r = rowIdx + 1;
    line.forEach((value, c) => {
      const style = { border: EXPORT_THIN_BORDERS };
      if (typeof value === "number") {
        style.alignment = { horizontal: "right" };
        style.numFmt = "#,##0.00";
      }
      setStyle(r, c, style);
    });
  });

  return worksheet;
};

const MyTable = ({
  columns,
  data,
  search,
  showTotal = false,
  setstartdate,
  setenddate,
  startdate,
  enddate,
  fetch,
  setLottoselect,
  lottoselect,
  fontSizeClass = "text-[14px]",
  // When every column is fitContent, forcing the table to w-full leaves no
  // flexible column to absorb the leftover width — auto table layout then
  // spreads that leftover evenly across all the "1%" columns instead of
  // shrinking them, which is the opposite of what fitContent is for. Opt in
  // per table so pages that actually want a full-width table (most of them)
  // are unaffected.
  autoWidth = false,
}) => {
  // console.log(data)
  const { t } = useLanguage();
  const location = useLocation();
  const drillDownParams = new URLSearchParams(location.search);
  const seniorVal = drillDownParams.get("senior");
  const masterVal = drillDownParams.get("master");
  const agentVal = drillDownParams.get("agent");

  const drillDownCrumbs = [];
  if (seniorVal) {
    drillDownCrumbs.push({
      label: seniorVal,
      href: `/agent?username=${seniorVal}&level=master&senior=${seniorVal}`,
    });
  }
  if (masterVal) {
    drillDownCrumbs.push({
      label: masterVal,
      href: `/agent?username=${masterVal}&level=agent&senior=${seniorVal}&master=${masterVal}`,
    });
  }
  if (agentVal) {
    drillDownCrumbs.push({
      label: agentVal,
      href: `/member?username=${agentVal}&level=member&senior=${seniorVal}&master=${masterVal}&agent=${agentVal}`,
    });
  }

  const [currentRow, setCurrentRow] = useState(0);
  const [selectedSortOption] = useState("");
  const [searchQuery1, setSearchQuery1] = useState("");
  const [searchQuery2, setSearchQuery2] = useState("");
  const [searchQuery3, setSearchQuery3] = useState([]);

  const [SearchDateValue, setSearchDateValue] = useState("");
  const [SearchDatetoValue, setSearchDatetoValue] = useState("");

  const [filteredTableData, setFilteredTableData] = useState(
    Array.isArray(data) ? data : [],
  );
  const [exporting, setExporting] = useState(false);
  const itemsPerPage = 1; // Display one row at a time

  // Exports exactly what's currently filtered/visible in this table
  // (filteredTableData), reading raw values straight off the columns'
  // accessors so it stays in sync with whatever columns the caller passes.
  const handleExportExcel = () => {
    if (filteredTableData.length === 0) {
      toast.info(t("common_no_data"), { autoClose: 2000 });
      return;
    }
    setExporting(true);
    try {
      const worksheet = buildTableExportWorksheet(columns, filteredTableData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Agent");
      XLSX.writeFile(
        workbook,
        `agent-list-${moment().format("YYYYMMDD_HHmm")}.xlsx`,
      );
    } catch (error) {
      toast.error(t("common_load_error"), { autoClose: 2000 });
    } finally {
      setExporting(false);
    }
  };

  //ช่องค้นหาตารางรายละเอียดรายได้
  const lotto_type = [
    { labelKey: "table_lotto_thai", value: "th_gov" },
    { labelKey: "table_lotto_laos", value: "laos_gov" },
    { labelKey: "table_lotto_yeekee5", value: "Yeekee_5" },
    { labelKey: "table_lotto_yeekee10", value: "Yeekee_10" },
    { labelKey: "table_lotto_yeekee15", value: "Yeekee_15" },
  ];
  const status_type = [
    { labelKey: "table_status_paid", value: "clear" },
    { labelKey: "table_status_unpaid", value: "not_clear" },
  ];
  const bet_type = [
    { value: "first", labelKey: "table_bet_first" },
    { value: "two_bot", labelKey: "table_bet_two_bot" },
    { value: "run_top", labelKey: "table_bet_run_top" },
    { value: "two_top", labelKey: "table_bet_two_top" },
    { value: "three_front", labelKey: "table_bet_three_front" },
    { value: "run_bot", labelKey: "table_bet_run_bot" },
    { value: "three_tod", labelKey: "table_bet_three_tod" },
    { value: "three_top", labelKey: "table_bet_three_top" },
  ];

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: filteredTableData, // Use filteredTableData here
        initialState: {
          sortBy: selectedSortOption
            ? [{ id: selectedSortOption, desc: true }]
            : [],
        },
      },
      useFilters,
      useGlobalFilter,
      useSortBy,
    );
  const RowCount = Math.ceil(rows.length / itemsPerPage);
  const displayRows = rows.slice(0, (currentRow + 1) * itemsPerPage);
  useEffect(() => {
    setFilteredTableData(Array.isArray(data) ? data : []);
  }, [data]);

  useEffect(() => {
    if (currentRow < RowCount - 1) {
      const timer = setTimeout(() => {
        setCurrentRow(currentRow + 1);
      }, 40); // Delay in milliseconds between rendering each row

      return () => clearTimeout(timer);
    }
  }, [currentRow, RowCount]);

  const handleSearch = () => {
    // Filter the table data based on searchQuery1 and searchQuery2
    const filteredData = data.filter((item) => {
      const match1 =
        searchQuery1.length === 0 ||
        searchQuery1.some((value) => item.lotto_type.includes(value));

      const match2 =
        searchQuery2.length === 0 ||
        searchQuery2.some((value) => item.bet_type.includes(value));

      return match1 && match2;
    });

    // Update the filteredTableData state with the filtered data
    setFilteredTableData(filteredData);
  };
  const handleSearchMember = () => {
    // Filter the table data based on searchQuery1 and searchQuery2
    const filteredData = data.filter((item) => {
      const match1 =
        searchQuery1 === "" || item.Username.includes(searchQuery1);
      const match2 = searchQuery2 === "" || item.phone.includes(searchQuery2);
      const match3 =
        searchQuery3 === "" || item.bankaccoutnumber.includes(searchQuery3);
      return match1 && match2 && match3;
    });

    // Update the filteredTableData state with the filtered data
    setFilteredTableData(filteredData);
  };

  const handleSearchAgent = () => {
    // Filter the table data based on searchQuery1 and searchQuery2
    const filteredData = data.filter((item) => {
      const match1 =
        searchQuery1 === "" || item.username.includes(searchQuery1);
      const match2 =
        searchQuery2 === "" || item.upline_username.includes(searchQuery2);
      return match1 && match2;
    });

    // Update the filteredTableData state with the filtered data
    setFilteredTableData(filteredData);
  };

  const handleSearchPaidHistory1 = () => {
    // Make a copy of the original data
    let dataCopy = [...data];

    // Convert searchQuery2 to an integer
    const searchQuery2Int = parseInt(searchQuery2);

    // Filter the dataCopy based on the single search input
    let filteredData = dataCopy.filter((item) => {
      // console.log(item.id, searchQuery2Int);
      const match1 = searchQuery1 === "" || item.agent.includes(searchQuery1);
      const match2 = isNaN(searchQuery2Int) || item.id === searchQuery2Int;

      // Use logical operators (e.g., OR) to define the relationship between search fields
      // If either search field matches, it includes the item
      return match1 || match2;
    });

    // Sort the filteredData by the 'id' field if searchQuery2 is not empty
    if (searchQuery1 !== "") {
      filteredData = filteredData.filter((item) => item.agent === searchQuery1);
    }

    if (searchQuery2 !== "") {
      filteredData = filteredData.filter((item) => item.id === searchQuery2Int);
    }
    // If searchQuery3 is an array and not empty, filter the data based on its values
    if (Array.isArray(searchQuery3) && searchQuery3.length > 0) {
      filteredData = filteredData.filter((item) =>
        searchQuery3.includes(item.status),
      );
    }
    // If SearchDateValue and SearchDatetoValue are not empty and valid date strings, filter data within the date range
    if (SearchDateValue && SearchDatetoValue) {
      const startDate = new Date(SearchDateValue);
      const endDate = new Date(SearchDatetoValue);

      filteredData = filteredData.filter((item) => {
        const buyDate = new Date(item.billcutdate);
        return buyDate >= startDate && buyDate <= endDate;
      });
    }

    // Update the filteredTableData state with the filtered and sorted data
    setFilteredTableData(filteredData);
  };

  const handleSearchQuery1Change = (value) => {
    if (searchQuery1.includes(value)) {
      setSearchQuery1(searchQuery1.filter((item) => item !== value));
    } else {
      setSearchQuery1([...searchQuery1, value]);
    }
  };

  const handleSearchQuery2Change = (value) => {
    if (searchQuery2.includes(value)) {
      setSearchQuery2(searchQuery2.filter((item) => item !== value));
    } else {
      setSearchQuery2([...searchQuery2, value]);
    }
  };

  const handleSearchQuery3Change = (value) => {
    if (lottoselect.includes(value)) {
      setLottoselect(lottoselect.filter((item) => item !== value));
    } else {
      setLottoselect([...lottoselect, value]);
    }
  };

  const getTotalAmount = () => {
    const filteredData =
      searchQuery1 || searchQuery2 ? filteredTableData : data;

    if (!Array.isArray(filteredData) || filteredData.length === 0)
      return { totalAmount: 0, totalCommission: 0 };

    let totalAmount = [];
    // Calculate the total amount for filtered data
    const totalSaleAmount = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_sale), 0)
          .toFixed(2)}
      </span>
    );
    const totalCommission = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.commission), 0)
          .toFixed(2)}
      </span>
    );
    const totalTotal_profit = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_profit), 0)
          .toFixed(2)}
      </span>
    );
    const totalTotal_lose = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_lose), 0)
          .toFixed(2)}
      </span>
    );
    const totalAgent_take = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.agent_take), 0)
          .toFixed(2)}
      </span>
    );
    const totalupline_take = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.upline_take), 0)
          .toFixed(2)}
      </span>
    );

    totalAmount.push(
      totalSaleAmount,
      totalCommission,
      totalTotal_profit,
      totalTotal_lose,
      totalAgent_take,
      totalupline_take,
    );

    return totalAmount;
  };
  const getTotalPaidhistory1 = () => {
    const filteredData =
      searchQuery1 || searchQuery2 ? filteredTableData : data;

    if (!Array.isArray(filteredData) || filteredData.length === 0)
      return { totalAmount: 0, totalCommission: 0 };

    let totalAmount = [];
    // Calculate the total amount for filtered data
    const totalSaleAmount = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_sale), 0)
          .toFixed(2)}
      </span>
    );
    const totalLose = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_lose), 0)
          .toFixed(2)}
      </span>
    );
    const totalCommission = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.total_commission), 0)
          .toFixed(2)}
      </span>
    );
    const totalTotal_sale = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce(
            (total, item) =>
              total + parseInt(item.total_sale - item.total_lose),
            0,
          )
          .toFixed(2)}
      </span>
    );
    const totalAgent_take = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.agent_take), 0)
          .toFixed(2)}
      </span>
    );
    const totalupline_take = (
      <span className="p-2 font-bold text-brand-blue-600">
        {filteredData
          .reduce((total, item) => total + parseInt(item.upline_take), 0)
          .toFixed(2)}
      </span>
    );

    totalAmount.push(
      totalSaleAmount,
      totalLose,
      totalCommission,
      totalTotal_sale,
      totalAgent_take,
      totalupline_take,
    );

    return totalAmount;
  };
  const totalAmountDataPaidHistory1 = getTotalPaidhistory1();

  const totalAmountData = getTotalAmount();

  const getTotalAmountWinlose = () => {
    const filteredData =
      searchQuery1 ||
      searchQuery2 ||
      searchQuery3 ||
      SearchDateValue ||
      SearchDatetoValue
        ? filteredTableData
        : data;

    if (!Array.isArray(filteredData) || filteredData.length === 0)
      return { totalAmount: 0, totalCommission: 0 };

    // Calculate the total amount for filtered data
    const sumField = (field) =>
      filteredData.reduce(
        (total, item) => total + (parseFloat(item[field]) || 0),
        0,
      );

    const renderTotal = (field) => {
      const total = sumField(field);
      return (
        <span
          className={`p-2 font-bold ${total < 0 ? "text-red-600" : "text-blue-600"}`}
        >
          {total.toFixed(2)}
        </span>
      );
    };

    return [
      renderTotal("sum(bet)"),
      renderTotal("commission"),
      renderTotal("win_lose"),
      renderTotal("agent_take"),
      renderTotal("master_win_lose"),
      renderTotal("master_commission"),
      renderTotal("master_total"),
      renderTotal("pay_out"),
    ];
  };

  const totalAmountDataWinlose = getTotalAmountWinlose();

  return (
    <div className="p-2">
      {search === "IncomeData" && (
        <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg">
          <div className="mb-4 p-2 flex items-center">
            <div className="flex flex-row items-center mr-4">
              <p className="font-bold mr-2">{t("table_filter_by_lotto_type")}</p>
              {lotto_type.map((item) => (
                <label key={item.value} className="block mr-2 p-1">
                  <input
                    className="p-1"
                    type="checkbox"
                    name="field1"
                    value={item.value}
                    checked={searchQuery1.includes(item.value)} // Use Array.includes to handle multiple selections
                    onChange={(e) => handleSearchQuery1Change(e.target.value)}
                  />
                  {t(item.labelKey)}
                </label>
              ))}
            </div>
            <div className="flex flex-row items-center">
              <p className="font-bold mr-2">{t("table_filter_by_bet_type")}</p>
              {bet_type.map((item) => (
                <label key={item.value} className="block mr-2 p-1">
                  <input
                    type="checkbox"
                    name="field2"
                    value={item.value}
                    checked={searchQuery2.includes(item.value)} // Use Array.includes to handle multiple selections
                    onChange={(e) => handleSearchQuery2Change(e.target.value)}
                  />
                  {t(item.labelKey)}
                </label>
              ))}
            </div>
            <button
              onClick={handleSearch}
              className="py-1 px-2 bg-blue-500 text-white rounded"
            >
              {t("table_search_button")}
            </button>
          </div>
        </div>
      )}
      {search === "Winlose" && (
        <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg">
          <div className="mb-4 p-2 flex items-center">
            <div className="flex flex-col md:flex flex-row items-center">
              <div className="flex flex-row mr-4">
                <label className="font-bold mb-2">{t("table_filter_by_lotto_type")}</label>
                {lotto_type.map((item) => (
                  <label key={item.value} className="block mr-2 p-1">
                    <input
                      className="p-1"
                      type="checkbox"
                      name="field1"
                      value={item.value}
                      checked={lottoselect.includes(item.value)}
                      onChange={(e) => handleSearchQuery3Change(e.target.value)}
                    />
                    {t(item.labelKey)}
                  </label>
                ))}
              </div>
              <div className="p-2 border-[1px] border-slate-300 rounded-lg">
                <div className="flex flex-col mr-4">
                  <label className="font-bold mb-2">{t("table_from_date")}</label>
                  <div className="flex flex-row space-x-2">
                    <input
                      type="date" // Use type "date" for date input
                      className="p-1 border border-gray-300 rounded"
                      value={startdate} // Set the date input value from state
                      onChange={(e) => setstartdate(e.target.value)} // Update the date input value to state
                    />
                    <label className="font-bold mb-2">-</label>
                    <input
                      type="date" // Use type "date" for date input
                      className="p-1 border border-gray-300 rounded"
                      value={enddate} // Set the date input value from state
                      onChange={(e) => setenddate(e.target.value)} // Update the date input value to state
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={() => fetch()}
                className="py-1 px-2 mt-2 ml-2 bg-blue-500 text-white rounded"
              >
                {t("table_search_button")}
              </button>
            </div>
          </div>
        </div>
      )}
      {search === "PaidHistory1" && (
        <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg">
          <div className="mb-4 p-2 flex items-center">
            <div className="flex flex-col items-center mr-4">
              <div className="flex flex-col mr-4">
                <label className="font-bold mb-2">{t("table_search_username")}</label>
                <input
                  type="text"
                  className="p-1 border border-gray-300 rounded"
                  value={searchQuery1}
                  onChange={(e) => setSearchQuery1(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex flex-row items-center">
              <div className="flex flex-row mr-4">
                <label className="font-bold mb-2 mr-2">{t("table_filter_by_payment_status")}</label>
                {status_type.map((item) => (
                  <label key={item.value} className="block mr-2 p-1">
                    <input
                      className="p-1"
                      type="checkbox"
                      name="field1"
                      value={item.value}
                      checked={searchQuery3.includes(item.value)}
                      onChange={(e) => handleSearchQuery3Change(e.target.value)}
                    />
                    {t(item.labelKey)}
                  </label>
                ))}
              </div>
              <div className="p-2 border-[1px] border-slate-300 rounded-lg">
                <div className="flex flex-col mr-4">
                  <label className="font-bold mb-2">{t("table_from_date")}</label>
                  <div className="flex flex-row space-x-2">
                    <input
                      type="date" // Use type "date" for date input
                      className="p-1 border border-gray-300 rounded"
                      value={SearchDateValue} // Set the date input value from state
                      onChange={(e) => setSearchDateValue(e.target.value)} // Update the date input value to state
                    />
                    <label className="font-bold mb-2">-</label>
                    <input
                      type="date" // Use type "date" for date input
                      className="p-1 border border-gray-300 rounded"
                      value={SearchDatetoValue} // Set the date input value from state
                      onChange={(e) => setSearchDatetoValue(e.target.value)} // Update the date input value to state
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={handleSearchPaidHistory1}
                className="py-1 px-2 mt-2 ml-2 bg-blue-500 text-white rounded"
              >
                {t("table_search_button")}
              </button>
            </div>
          </div>
        </div>
      )}
      {search === "MemberData" && (
        <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg text-xs">
          <div className="p-2 flex items-center">
            <div className="items-center mr-4">
              <p className="font-bold mr-2 text-xs">{t("table_search_username")}</p>
              <input
                type="text"
                className="p-1 border border-gray-300 rounded text-xs"
                value={searchQuery1}
                onChange={(e) => setSearchQuery1(e.target.value)}
              />
            </div>
            <div className="items-center">
              <p className="font-bold mr-2 text-xs">{t("table_search_phone")}</p>
              <input
                type="text"
                className="p-1 border border-gray-300 rounded text-xs"
                value={searchQuery2}
                onChange={(e) => setSearchQuery2(e.target.value)}
              />
            </div>
            <div className="items-center ml-4">
              <p className="font-bold mr-2 text-xs">{t("table_search_account_number")}</p>
              <input
                type="text"
                className="p-1 border border-gray-300 rounded text-xs"
                value={searchQuery3}
                onChange={(e) => setSearchQuery3(e.target.value)}
              />
            </div>
            <button
              onClick={handleSearchMember}
              className="mt-4 ml-2 py-1 px-2 bg-blue-500 text-white rounded text-xs"
            >
              {t("table_search_button")}
            </button>
          </div>
        </div>
      )}
      {search === "AgentData" && (
        <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg">
          <div className="p-2 flex items-center">
            <div className="flex flex-row items-center mr-4">
              <p className="font-bold mr-2 text-xs">
                {t("table_search_agent_name")}
              </p>
              <input
                type="text"
                className="p-1 border border-gray-300 rounded text-xs"
                value={searchQuery1}
                onChange={(e) => setSearchQuery1(e.target.value)}
              />
            </div>
            <div className="flex flex-row items-center">
              <p className="font-bold mr-2 text-xs">
                {t("table_search_upline_name")}
              </p>
              <input
                type="text"
                className="p-1 border border-gray-300 rounded text-xs"
                value={searchQuery2}
                onChange={(e) => setSearchQuery2(e.target.value)}
              />
            </div>
            <button
              onClick={handleSearchAgent}
              className="py-1 px-2 bg-blue-500 text-white rounded text-xs"
            >
              {t("table_search_button")}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-1 py-1 px-2 ml-2 bg-green-600 text-white rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDownloadIcon sx={{ fontSize: 16 }} />
              {exporting ? t("common_exporting") : t("common_export_excel")}
            </button>
          </div>
        </div>
      )}
      <div className="rounded overflow-x-auto mt-1">
        {drillDownCrumbs.length > 0 && (
          <div className="px-2 py-1 text-sm font-semibold text-brand-blue-700 flex flex-wrap items-center gap-1">
            {drillDownCrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.href}>
                {index > 0 && <span className="text-brand-blue-400">\</span>}
                <a href={crumb.href} className="hover:underline">
                  {crumb.label}
                </a>
              </React.Fragment>
            ))}
          </div>
        )}
        <Table
          {...getTableProps()}
          className={`${autoWidth ? "" : "w-full"} ${fontSizeClass}`}
          sx={autoWidth ? { width: "auto" } : undefined}
        >
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...hgProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...hgProps} className="bg-gray-100">
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps(
                      column.getSortByToggleProps(),
                    );
                    return (
                      <th
                        key={colKey}
                        {...colProps}
                        className={`py-1 px-2 ${fontSizeClass} ${column.widthClass || ""} ${column.headerClassName || ""}`}
                        align={column.align}
                        style={
                          column.width
                            ? {
                                width: column.width,
                                minWidth: column.width,
                                maxWidth: column.width,
                              }
                            : column.fitContent
                              ? { width: "1%", whiteSpace: "nowrap" }
                              : undefined
                        }
                      >
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {displayRows.map((row, index) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              const rowClassName =
                index % 2 === 0 ? "bg-gray-50" : "bg-gray-200";
              return (
                <tr
                  key={rowKey}
                  {...rowProps}
                  className={`transition-colors ${rowClassName} hover:bg-gray-300`}
                >
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td
                        key={cellKey}
                        {...cellProps}
                        className={`py-1 px-2 ${cell.column.widthClass}`}
                        align={cell.column.align}
                        style={
                          cell.column.width
                            ? {
                                width: cell.column.width,
                                minWidth: cell.column.width,
                                maxWidth: cell.column.width,
                              }
                            : cell.column.fitContent
                              ? { width: "1%", whiteSpace: "nowrap" }
                              : undefined
                        }
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {showTotal === "Dashboard" && (
              <tr>
                <td colSpan={columns.length - 6} align="right">
                  <span className="p-2 font-bold text-brand-blue-600">
                    {t("table_total")}
                  </span>
                </td>
                {Object.values(totalAmountData).map((value, index) => (
                  <td key={index}>{value}</td>
                ))}
              </tr>
            )}
            {showTotal === "Winlose" && (
              <tr>
                <td colSpan={5} align="right">
                  <span className="p-2 font-bold text-brand-blue-600">
                    {t("table_total")}
                  </span>
                </td>
                {Object.values(totalAmountDataWinlose).map((value, index) => (
                  <td align="right" key={index}>
                    {value}
                  </td>
                ))}
              </tr>
            )}
            {showTotal === "Paidhistory1" && (
              <tr>
                <td colSpan={columns.length - 7} align="right">
                  <span className="p-2 font-bold text-brand-blue-600">
                    {t("table_total")}
                  </span>
                </td>
                {Object.values(totalAmountDataPaidHistory1).map(
                  (value, index) => (
                    <td key={index}>{value}</td>
                  ),
                )}
              </tr>
            )}
            {showTotal === "Ordertable" && (
              <tr>
                <td colSpan={columns.length - 2} align="right">
                  <span className="p-2 font-bold text-brand-blue-600">
                    {t("table_total")}
                  </span>
                </td>
                {Object.values(totalAmountDataWinlose).map((value, index) => (
                  <td key={index}>{value}</td>
                ))}
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default MyTable;
